import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Router from 'next/router';
import LoginForm from './form';
import { CAPTCHA_CONFIG, CAPTCHA_FAILURE_COPY } from '@/constants/authentication';
import { type RootState } from '@/types/app';
import { type IUserLogin } from '@/types/authentication';
import { parseReferrer, tryCatch } from '@/utils/global';
import authActions from '@/redux/auth/actions';
import globalActions from '@/redux/global/actions';
import useCaptcha from '@/utils/useCaptcha';
import { useClearConnectionErrorsOnMount } from '@/hooks/useClearConnectionErrorsOnMount';

const LoginComponent = () => {
  const dispatch = useDispatch();
  const { fetchCaptchaToken, captchaEnabled, loadingCaptcha } = useCaptcha({
    action: CAPTCHA_CONFIG.API_ACTIONS.LOGIN,
  });

  const loading = useSelector((state: RootState) => state.app.auth.loading);
  const connectionError = useSelector((state: RootState) => state.app.auth.loginConnectionError);
  const loginFailed = useSelector((state: RootState) => state.app.auth.loginFailed);

  /**
   * TEAM-14320: `loadingCaptcha` only reports "the reCAPTCHA script hasn't loaded yet"
   * (utils/useCaptcha.tsx) — it is false for the whole duration of `fetchCaptchaToken()`,
   * and Redux `loading` isn't set until `login` is dispatched after it. Without this lock
   * the banner's Try again and (TEAM-14519) the submit button stay enabled through that
   * window and a second press fires a second login. The same idea as the sign-up form's
   * lock (signUpForm/index.tsx). Redux `loading` can't do this job: `CHECKOUT_AS_GUEST`
   * clears it in the same batch that `login` sets it.
   */
  const [submitting, setSubmitting] = useState(false);

  /**
   * Re-enable once the attempt fails, so the diner can retry: a connection failure raises
   * the banner, and any other rejected login sets `loginFailed` (TEAM-14519). No success
   * reset is needed — a successful login navigates away, or at checkout the Sign In tab
   * unmounts once `me.id` is set.
   */
  useEffect(() => {
    if (connectionError || loginFailed) {
      setSubmitting(false);
    }
  }, [connectionError, loginFailed]);

  useClearConnectionErrorsOnMount('login');

  const loginFactory = (formValues: IUserLogin | undefined, captchaResponse?: string | null) => {
    dispatch(
      authActions.login(
        Object.assign({}, formValues, {
          [CAPTCHA_CONFIG.FIELD_NAME]: captchaResponse,
        }),
        parseReferrer(Router),
        false,
        Router.pathname,
      ),
    );
  };

  const submitLogIn = async (formData: IUserLogin | undefined) => {
    setSubmitting(true);
    const [captchaResponse, captchaError] = captchaEnabled
      ? await tryCatch(fetchCaptchaToken())
      : [null, null];
    if (captchaError) {
      // TEAM-14519: coo-api can only refuse a login sent without the token it requires, so
      // don't send one. The helper has already reported the failure.
      dispatch(globalActions.displayErrorToast(CAPTCHA_FAILURE_COPY, true, true));
      setSubmitting(false);
      return;
    }
    loginFactory(formData, captchaResponse);
    dispatch(authActions.checkoutAsGuest(null));
  };

  return (
    <LoginForm
      onLogin={(formData) => void submitLogIn(formData)}
      loading={loading}
      loadingCaptcha={loadingCaptcha}
      connectionError={connectionError}
      submitting={submitting}
    />
  );
};

export default LoginComponent;
